import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { LINKS } from "@/data";

const WhatsAppIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.48-.5-.67-.5h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.28-.2-.57-.35zM12.05 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88zm8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L0 24l6.3-1.65a11.9 11.9 0 0 0 5.74 1.46h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.17-3.49-8.42z" />
  </svg>
);

export default function TrackShare({ track, index }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const text = `"${track.title}" — Linea Cremisi. Ascolta qui: ${track.spotify}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(text)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const shareInstagram = async () => {
    const ok = await copy();
    toast(
      ok
        ? "Link copiato — incollalo nella tua storia Instagram"
        : "Apri Instagram e tagga @lineacremisi",
      { description: track.title }
    );
    window.open(LINKS.instagram, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const copyLink = async () => {
    const ok = await copy();
    toast(ok ? "Link copiato" : "Impossibile copiare il link", {
      description: track.title,
    });
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        data-testid={`track-share-toggle-${index + 1}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Condividi ${track.title}`}
        className={`flex h-[30px] w-[30px] items-center justify-center border transition-colors duration-200 ${
          open
            ? "border-red-600 text-red-500"
            : "border-neutral-800 text-neutral-500 hover:border-red-700 hover:text-red-500"
        }`}
      >
        <Share2 className="h-3 w-3" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`track-share-menu-${index + 1}`}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-full z-20 mt-2 w-56 border border-red-900/50 bg-[#0B0B0B] p-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.7)]"
          >
            <p className="px-3 pb-2 pt-1.5 font-mono-tac text-[9px] uppercase tracking-[0.3em] text-neutral-500">
              Condividi il brano
            </p>
            <a
              data-testid={`track-share-whatsapp-${index + 1}`}
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-200 transition-colors duration-150 hover:bg-red-950/30 hover:text-red-400"
            >
              <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
              WhatsApp
            </a>
            <button
              data-testid={`track-share-instagram-${index + 1}`}
              onClick={shareInstagram}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-neutral-200 transition-colors duration-150 hover:bg-red-950/30 hover:text-red-400"
            >
              <Instagram className="h-4 w-4 text-[#E1306C]" />
              Instagram
              <span className="ml-auto font-mono-tac text-[9px] uppercase tracking-[0.2em] text-neutral-600">
                copia + apri
              </span>
            </button>
            <button
              data-testid={`track-share-copy-${index + 1}`}
              onClick={copyLink}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-neutral-200 transition-colors duration-150 hover:bg-red-950/30 hover:text-red-400"
            >
              <Link2 className="h-4 w-4 text-neutral-400" />
              Copia link
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
